import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';
import isEqual from 'react-fast-compare';

import { get, heightFix, pageContext } from 'ctm-util-functions';
import { connect } from 'react-redux';
import animateScrollTo from 'animated-scroll-to';
import { resultsConfigSelector } from 'ctm-selectors';
import { SimpleFlexbox } from '../../../flexbox/flexbox';
import { ResultsControls } from '../../results_controls/results_controls';
import { FeaturesColumn } from './features_column/features_column';
import { FeaturesHeaderAffixedWrapper } from './features_column/features_header_affixed_wrapper/features_header_affixed_wrapper';

import styles from './features.css';

/**
 * A generic modal container that formats, prepares and passes props to our visual modal component
 */
class _Features extends Component {

    constructor(props) {
        super(props);

        // Animate to top of page
        animateScrollTo(0);

        this.state = {
            columnsPerPage: get(this.props, `columnsPerPage.${get(this.props, 'breakpoint')}`, 3)
        };
    }

    /**
     * For now, re run the height fixing
     */
    componentDidUpdate = () => {
        this.checkColumnsPerPage();
        heightFix('balanceheight');
    };

    /**
     * For now, re run the height fixing
     */
    componentDidMount = () => {
        heightFix('balanceheight');
    };

    /**
     * Simply checks first whether we have column per page config, then update the columns based on the breakpoint
     * change
     */
    checkColumnsPerPage = () => {
        const { columnsPerPage, breakpoint } = this.props;

        // if a 'columnsPerPage' schema config object has been supplied
        if (typeof columnsPerPage !== 'undefined') {
            // also if we have specific config for the current breakpoint (it should always be a positive number)
            if (columnsPerPage[breakpoint]) {
                // if the columns per page number differs from the current columns per page number
                // eslint-disable-next-line react/destructuring-assignment
                if (columnsPerPage[breakpoint] !== this.state.columnsPerPage) {
                    // update the state to render the correct number of columns per page based on the config object
                    this.setState({ columnsPerPage: columnsPerPage[breakpoint] });
                }
            }
        }
    };

    /**
     * Check if the number of quotes has changed, if they have and we're on a page that no longer exists, then reset to the first page
     * @param {Object} nextProps - The incoming set of props.
     */
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const numQuotes = nextProps.quotes.size;
        const { quotes, dispatch } = this.props;
        const { columnsPerPage } = this.state;
        if (numQuotes !== quotes.size) {
            const pageNumber = nextProps.pageNumber;
            const numPages = Math.ceil(numQuotes / columnsPerPage);
            if (pageNumber > numPages) {
                dispatch({ type: 'SET_RESULTS_CONFIG_VALUE', key: 'pageNumber', value: 1 });
            }
        }
    };

    // @TODO - Is this really needed?
    // Rerenders in this component are critical for the 'heightfix' to occur
    // If the rerender is constrained, it means there is more chance of misalignments in the columns
    /**
     * Don't re-render unless pageNumber or compareModeActive have changed
     * @param {Object} nextProps - The incoming set of props
     * @param {Object} nextState - The incoming component state
     * @returns {Boolean} - True if the component should re-render
     */
    // shouldComponentUpdate = (nextProps, nextState) => {
    //     const { columnsPerPage } = this.state;
    //     const {
    //         breakpoint,
    //         pageNumber,
    //         compareModeActive,
    //         quotes,
    //         accordionisOpen
    //     } = this.props;

    //     return nextProps.breakpoint !== breakpoint
    //         || nextState.columnsPerPage !== columnsPerPage
    //         || nextProps.pageNumber !== pageNumber
    //         || nextProps.compareModeActive !== compareModeActive
    //         || !isEqual(nextProps.quotes, quotes)
    //         || nextProps.accordionisOpen !== accordionisOpen;
    // };

    /**
     * Renders the Features Affix Headers for results
     * @param {Array} quotes - The array of quotes to render within the affixed header
     * @param {Boolean} label - whether or not to render a label cell
     * @returns {Node} a FeaturesHeaderAffixedWrapper for the affixed result column headers
     */
    renderFeaturesAffixHeadersWrapper = (quotes, label = true) => {
        const { compareModeActive } = this.props;
        return (
            <FeaturesHeaderAffixedWrapper
                quotes={quotes}
                label={label}
                compareModeActive={compareModeActive}
            />
        );
    };

    /**
     * Renders Features column from quotes and renders unAvailable Quotes based on renderUnAvailable
     * @param {Array} quotes - The array of quotes to render within the Features Container
     * @param {Boolean} fullWidthLabels - should FeaturesColumn render with full width labels
     * @returns {Array} - an Array of FeaturesColumns
     */
    renderFeaturesColumns = (quotes, fullWidthLabels = false) => {
        const { unavailableBrands, filteredQuotes, compareModeActive } = this.props;
        const { columnsPerPage } = this.state;
        const showFilteredQuotesHeader = filteredQuotes !== undefined && filteredQuotes.size > 0;
        let hasRenderedUnavailable = false;

        return quotes.map((quote, index) => {
            if (quote) {
                return (
                    <FeaturesColumn
                        index={index}
                        id={`result-column-${index}`}
                        key={quote.getIn(['productMeta', 'id'])}
                        quote={quote}
                        headerLayout="component_layoutFeatureHeaderCell"
                        fullWidthLabels={fullWidthLabels}
                        columnsPerPage={columnsPerPage}
                        numberOfQuotes={quotes.reduce((acc, curr) => {
                            return typeof curr !== 'undefined' ? acc + 1 : acc;
                        }, 0)}
                    />
                );
            }
            if (!compareModeActive && !hasRenderedUnavailable) {
                hasRenderedUnavailable = true;
                return (
                    <Fragment key="filtered_unavailable">
                        {showFilteredQuotesHeader
                        && <FeaturesColumn key="filtered_products" filteredQuotes={filteredQuotes} />}
                        <FeaturesColumn key="unavailable_brands" unavailableBrands={unavailableBrands} />
                    </Fragment>
                );
            }
            return null;
        });
    };

    /**
     * Get the quotes to render based on the page number, and number of quotes to display per page
     * @param {Array} quotes - all of the quotes, unavailable and available
     * @returns {Array} an array of available quotes, with a length of columnsPerPage, and an offset to suit the pagination and page number
     */
    getQuotesToRender = (quotes) => {
        // the current page and columnsPerPage local state
        const { columnsPerPage } = this.state;
        const { pageNumber } = this.props;

        // quotes per page and pagination logic
        return Array
            .apply(this, { length: columnsPerPage })
            .map((val, i) => quotes.get(i + ((pageNumber - 1) * columnsPerPage)));
    };

    /**
     * Render breakpoint specific results features configurations
     * @param {String } breakpoint - the breakpoint as a string
     * @returns {Node} a breakpoint specific features configuration
     */
    renderFeaturesForBreakpoint = (breakpoint) => {
        const { quotes, gridProps, compareModeActive } = this.props;
        const quotesToRender = this.getQuotesToRender(quotes);
        const featureContainerCompare = compareModeActive ? styles.featuresContainerCompare : null;
        const wrapperClassName = classNames(styles.featuresContainer, featureContainerCompare);

        switch (breakpoint) {
            case 'xs':
            case 'sm':
            case 'md':
            case 'lg': {
                return (
                    <SimpleFlexbox {...gridProps} className={wrapperClassName}>
                        { this.renderFeaturesAffixHeadersWrapper(quotesToRender, false) }
                        { this.renderFeaturesColumns(quotesToRender, true) }
                    </SimpleFlexbox>
                );
            }
            // xl and xxl
            default: {
                return (
                    <SimpleFlexbox xl={breakpoint === 'xl' ? gridProps.xl : false} className={wrapperClassName}>
                        { this.renderFeaturesAffixHeadersWrapper(quotesToRender) }
                        <FeaturesColumn quote={quotes.first()} isLabels headerLayout="component_layoutFeatureHeaderLabel" />
                        {this.renderFeaturesColumns(quotesToRender)}
                    </SimpleFlexbox>
                );
            }
        }
    };

    /**
     * Render the features mode results
     * @returns {Node|null} the configured features mode columns
     */
    render = () => {
        const { quotes, breakpoint, unavailableBrands, compareModeActive } = this.props;
        const { columnsPerPage } = this.state;

        const pagination = {
            range: columnsPerPage,
            total: quotes.size,
            unavailableBrands: Boolean(unavailableBrands.size) && !compareModeActive
        };

        return (
            <div id="resultsContainer">
                <ResultsControls
                    layout="component_layoutResultsControlFeatures"
                    pagination={pagination}
                />
                { this.renderFeaturesForBreakpoint(breakpoint) }
            </div>
        );
    };

}

_Features.propTypes = {
    quotes: PropTypes.instanceOf(Immutable.List).isRequired,
    pageNumber: PropTypes.number.isRequired,
    unavailableBrands: PropTypes.instanceOf(Immutable.List),
    filteredQuotes: PropTypes.instanceOf(Immutable.List),
    breakpoint: PropTypes.string.isRequired,
    columnsPerPage: PropTypes.object,
    gridProps: PropTypes.shape({
        xs: PropTypes.number,
        sm: PropTypes.number,
        md: PropTypes.number,
        lg: PropTypes.number,
        xl: PropTypes.number
    }).isRequired,
    compareModeActive: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    accordionisOpen: PropTypes.bool
};

const mapStateToProps = (state, props) => {
    const resultsConfig = resultsConfigSelector(state);
    const compareModeActive = resultsConfig.get('compareModeActive') || false;
    const pageNumber = resultsConfig.get('pageNumber');
    const accordionisOpen = resultsConfig.get('accordionState');

    return {
        pageNumber,
        compareModeActive,
        accordionisOpen
    };
};
const Features = connect(mapStateToProps)(_Features);
export { Features, _Features };
